import React, { useEffect, useMemo, useState } from 'react';
import { HDate } from '@hebcal/core';
import { BOAPI } from "../service/UsersService";

const EXT_LABEL = { jpg: 'תמונה', jpeg: 'תמונה', png: 'תמונה', video: 'וידאו', youtube: 'וידאו', pdf: 'PDF', text: 'טקסט', decision: 'החלטה', quiz: 'מבחן', weeklypdf: 'חוברת שבועית', tehillim: 'תהילים' };

const TYPE_OPTIONS = [
  { value: 'jpg', label: 'תמונה' },
  { value: 'video', label: 'וידאו' },
  { value: 'pdf', label: 'PDF' },
  { value: 'text', label: 'טקסט' },
  { value: 'decision', label: 'החלטה' },
  { value: 'weeklypdf', label: 'חוברת שבועית (לינק להורדה)' },
  { value: 'tehillim', label: 'תהילים' },
];

const GENDER_OPTIONS = [
  { value: '', label: 'הכל' },
  { value: 'זכר', label: 'זכר' },
  { value: 'נקבה', label: 'נקבה' },
];

const resolveKind = (ext) => {
  if (ext === 'pdf') return 'pdf';
  if (['youtube', 'yt', 'video', 'mp4', 'mov', 'webm'].includes(ext)) return 'video';
  if (ext === 'text') return 'text';
  if (ext === 'decision') return 'decision';
  if (ext === 'tehillim') return 'tehillim';
  return 'image';
};

// ── תקציר קהל היעד לתצוגה בטבלה — "כולם" אם אין שום הגבלה, אחרת רק הממדים
// שבפועל מוגבלים (מגדר/גיל/מותגים) מחוברים בפסיקים. ──
const describeTargeting = (item) => {
  const parts = [];
  if (item.targetGender) parts.push(item.targetGender);
  if (item.targetMinAge != null || item.targetMaxAge != null) {
    parts.push(`גיל ${item.targetMinAge ?? '0'}-${item.targetMaxAge ?? '∞'}`);
  }
  if (item.targetBrandIds) parts.push(`מותג ${item.targetBrandIds}`);
  return parts.length ? parts.join(', ') : 'כולם';
};

const pad = (n) => String(n).padStart(2, '0');

const toDateInput = (date) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// תאריכי הצגה הם ימים שלמים: התחלה = תחילת היום (00:00), סיום = סוף היום (23:59) — כלול.
const toSqlStart = (dateValue) => `${dateValue} 00:00`;
const toSqlEnd = (dateValue) => `${dateValue} 23:59`;

// ── תצוגת "לוח שנה" (כמו בטאב הודעות/פרסומות) — אותו דפוס בדיוק כמו HakdashotTab.jsx ──
const getHebrewDate = (date) => {
  if (!date || isNaN(date.getTime())) return '';
  return new HDate(date).renderGematriya().replace(/[֑-ׇ]/g, '');
};

const isSameDay = (a, b) => toDateInput(a) === toDateInput(b);

const MONTH_NAMES = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const WEEKDAY_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

// בונה את משבצות הלוח לחודש הנתון — כולל ימים "עודפים" מהחודש הקודם/הבא כדי למלא
// שבועות מלאים (6 שורות קבועות, כדי שהלוח לא "יקפוץ" בגובה בין חודשים).
const buildMonthGrid = (monthAnchor) => {
  const year = monthAnchor.getFullYear();
  const month = monthAnchor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = firstOfMonth.getDay(); // 0=ראשון
  const gridStart = new Date(year, month, 1 - startOffset);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
};

// מדיה נוספת בסטורי אחד — מגיעה מהשרת כמחרוזת JSON ([{type,url}]) בעמודה mediaJson.
const parseExtraMedia = (json) => {
  try {
    const arr = json ? JSON.parse(json) : [];
    return Array.isArray(arr) ? arr.map((m) => ({ type: m.type === 'video' ? 'video' : 'image', url: m.url || '' })) : [];
  } catch {
    return [];
  }
};

// רק image/video תומכים במדיה נוספת; מסננים שורות ריקות.
const cleanExtraMedia = (data, kind) =>
  kind === 'image' || kind === 'video'
    ? (data.extraMedia || []).filter((m) => m.url && m.url.trim()).map((m) => ({ type: m.type, url: m.url.trim() }))
    : [];

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// שדה קישור (URL) עם כפתור נוסף לבחירת קובץ מהמכשיר — מעלה ל-/api/file/upload
// (דרך BOAPI.uploadStoryFile) ומציב את הקישור המתקבל בתוך אותו שדה טקסט.
const UploadableUrlField = ({ label, value, onChange, placeholder, accept }) => {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
      const resp = await BOAPI.uploadStoryFile(base64, ext);
      if (resp?.code === 0 && resp.url) {
        onChange(resp.url);
      } else {
        window.alert(resp?.msg || 'העלאת הקובץ נכשלה');
      }
    } catch (err) {
      window.alert('העלאת הקובץ נכשלה');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={s.formGroup}>
      <label style={s.label}>{label}</label>
      <div style={s.uploadRow}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...s.input, flex: 1, minWidth: 0 }}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={s.uploadButton}
          disabled={uploading}
        >
          {uploading ? '⏳ מעלה...' : '📁 בחר קובץ'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={s.hiddenFileInput}
        />
      </div>
    </div>
  );
};

// "באוויר" = פעיל וגם בטווח התאריכים הנוכחי (לא רק isActive — גם סטורי פעיל
// יכול להיות עדיין לא התחיל או כבר הסתיים).
const isLive = (item) => {
  if (!item.isActive) return false;
  const now = new Date();
  return new Date(item.startDate) <= now && now <= new Date(item.endDate);
};

// ברירת מחדל לסטורי חדש: היום הנוכחי — מתחילת היום (00:00) ועד סופו (23:59).
const defaultNewStory = () => {
  const start = new Date();
  const end = start; // ברירת מחדל: היום הנוכחי בלבד — 00:00 עד 23:59 (ראו toSqlStart/toSqlEnd)
  return {
    name: '',
    ext: 'jpg',
    imageUrl: '',
    videoUrl: '',
    pdfUrl: '',
    description: '',
    startDate: toDateInput(start),
    endDate: toDateInput(end),
    // ── ברירת מחדל מפורשת "מתאים לכולם" (זכר+נקבה, גיל 0-100) — לא רק שדות
    // ריקים (שגם הם היו מתפרשים כ"בלי הגבלה", אבל פחות ברור למנהל בטופס). ──
    targetGender: '',
    targetMinAge: '0',
    targetMaxAge: '100',
    targetBrandIds: '',
    isCountable: false,
    countLabel: '',
    extraMedia: [],
    imageFit: '',
  };
};

const validateStory = (data) => {
  if (!data.name?.trim()) return 'נא להזין שם';
  if (!data.startDate || !data.endDate) return 'נא להזין תאריכי התחלה וסיום';
  if (new Date(data.endDate) < new Date(data.startDate)) return 'תאריך הסיום לא יכול להיות לפני תאריך ההתחלה';
  const kind = resolveKind(data.ext);
  if (kind !== 'text' && kind !== 'tehillim' && !data.imageUrl?.trim()) return 'נא להזין קישור לתמונה';
  if (kind === 'video' && !data.videoUrl?.trim()) return 'נא להזין קישור לוידאו';
  if (kind === 'pdf' && !data.pdfUrl?.trim()) return 'נא להזין קישור לקובץ PDF';
  if ((kind === 'text' || kind === 'decision' || kind === 'tehillim') && !data.description?.trim()) return 'נא להזין תוכן לסטורי';
  if (kind === 'decision' && data.isCountable && !data.countLabel?.trim()) return 'נא להזין תווית לספירה (למשל "יהודים הונח להם תפילין")';
  if (data.targetMinAge && data.targetMaxAge && Number(data.targetMinAge) > Number(data.targetMaxAge)) {
    return 'גיל מינימום לא יכול להיות גדול מגיל מקסימום';
  }
  return null;
};

// שדות הטופס המשותפים בין הוספה לעריכה.
const StoryFields = ({ data, onChange, isNew }) => {
  const kind = resolveKind(data.ext);
  return (
    <>
      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>שם *</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => onChange('name', e.target.value)}
            style={s.input}
            maxLength={32}
            placeholder="שם הסטורי"
          />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>סוג *</label>
          <select value={data.ext} onChange={(e) => onChange('ext', e.target.value)} style={s.input}>
            {!TYPE_OPTIONS.some((opt) => opt.value === data.ext) && (
              <option value={data.ext}>{EXT_LABEL[data.ext] || data.ext}</option>
            )}
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <UploadableUrlField
        label={kind === 'text' || kind === 'tehillim' ? 'קישור לתמונה ממוזערת (אופציונלי)' : 'קישור לתמונה *'}
        value={data.imageUrl}
        onChange={(value) => onChange('imageUrl', value)}
        placeholder="https://..."
        accept="image/*"
      />

      {kind === 'video' && (
        <UploadableUrlField
          label="קישור ליוטיוב / לוידאו *"
          value={data.videoUrl}
          onChange={(value) => onChange('videoUrl', value)}
          placeholder="https://youtube.com/..."
          accept="video/*"
        />
      )}

      {kind === 'pdf' && (
        <UploadableUrlField
          label="קישור לקובץ PDF *"
          value={data.pdfUrl}
          onChange={(value) => onChange('pdfUrl', value)}
          placeholder="https://..."
          accept="application/pdf"
        />
      )}

      {(kind === 'image' || kind === 'video') && (
        <div style={s.targetSection}>
          <div style={s.targetTitle}>תמונות/וידאו נוספים באותו סטורי (אופציונלי — הצופה מדפדף ביניהם)</div>
          {(data.extraMedia || []).map((m, i) => (
            <div key={i} style={s.extraMediaRow}>
              <select
                value={m.type}
                onChange={(e) => onChange('extraMedia', data.extraMedia.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))}
                style={{ ...s.input, width: 90, flex: '0 0 auto' }}
              >
                <option value="image">תמונה</option>
                <option value="video">וידאו</option>
              </select>
              <div style={{ flex: 1, minWidth: 0 }}>
                <UploadableUrlField
                  label=""
                  value={m.url}
                  onChange={(value) => onChange('extraMedia', data.extraMedia.map((x, j) => (j === i ? { ...x, url: value } : x)))}
                  placeholder="https://..."
                  accept={m.type === 'video' ? 'video/*' : 'image/*'}
                />
              </div>
              <button
                type="button"
                onClick={() => onChange('extraMedia', data.extraMedia.filter((_, j) => j !== i))}
                style={s.cancelButton}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange('extraMedia', [...(data.extraMedia || []), { type: 'image', url: '' }])}
            style={s.addExtraButton}
          >
            ➕ הוסף תמונה/וידאו
          </button>
        </div>
      )}

      {(kind === 'image' || kind === 'video') && (
        <div style={s.formGroup}>
          <label style={s.label}>{kind === 'video' ? 'תצוגת הוידאו כברירת מחדל' : 'תצוגת התמונה כברירת מחדל'}</label>
          <select value={data.imageFit || ''} onChange={(e) => onChange('imageFit', e.target.value)} style={s.input}>
            <option value="">{kind === 'video' ? 'אוטומטי (כל הוידאו, בלי חיתוך)' : 'אוטומטי (לפי יחס התמונה)'}</option>
            <option value="cover">{kind === 'video' ? 'לאורך (ממלא את המסך, קצוות עלולים להיחתך)' : 'לאורך (ממלאת את המסך)'}</option>
            <option value="contain">{kind === 'video' ? 'לרוחב (כל הוידאו)' : 'לרוחב (כל התמונה)'}</option>
          </select>
        </div>
      )}

      <div style={s.formGroup}>
        <label style={s.label}>{kind === 'text' || kind === 'decision' || kind === 'tehillim' ? 'תוכן הסטורי *' : 'כיתוב (אופציונלי)'}</label>
        <textarea
          value={data.description}
          onChange={(e) => onChange('description', e.target.value)}
          style={s.textarea}
          rows={3}
        />
      </div>

      {kind === 'decision' && (
        <div style={s.targetSection}>
          {isNew && (
            <div style={s.warningNote}>
              ⚠️ יצירת סטורי-החלטה חדש מכאן לא נתמכת עדיין במלואה — מומלץ ליצור החלטות חדשות מתוך האפליקציה, ולהשתמש בעריכה כאן רק כדי להפעיל/לעדכן ספירה על החלטה קיימת.
            </div>
          )}
          <label style={s.checkboxLabel}>
            <input
              type="checkbox"
              checked={!!data.isCountable}
              onChange={(e) => onChange('isCountable', e.target.checked)}
              style={s.checkbox}
            />
            אפשר ספירת משתתפים בהשלמה (למשל "יהודים הונח להם תפילין")
          </label>
          {data.isCountable && (
            <div style={s.formGroup}>
              <label style={s.label}>תווית הספירה *</label>
              <input
                type="text"
                value={data.countLabel}
                onChange={(e) => onChange('countLabel', e.target.value)}
                style={s.input}
                placeholder='למשל: "יהודים הונח להם תפילין"'
              />
            </div>
          )}
        </div>
      )}

      <div style={s.formRow}>
        <div style={s.formGroup}>
          <label style={s.label}>תחילת הצגה *</label>
          <input
            type="date"
            value={data.startDate}
            onChange={(e) => onChange('startDate', e.target.value)}
            style={s.input}
          />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>סוף הצגה *</label>
          <input
            type="date"
            value={data.endDate}
            onChange={(e) => onChange('endDate', e.target.value)}
            style={s.input}
          />
        </div>
      </div>

      <div style={s.targetSection}>
        <div style={s.targetTitle}>קהל יעד (אופציונלי — השאירו ריק כדי להציג לכולם)</div>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>מגדר</label>
            <select value={data.targetGender} onChange={(e) => onChange('targetGender', e.target.value)} style={s.input}>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>גיל מ-</label>
            <input
              type="number"
              min={0}
              value={data.targetMinAge}
              onChange={(e) => onChange('targetMinAge', e.target.value)}
              style={s.input}
              placeholder="לדוגמה 18"
            />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>גיל עד</label>
            <input
              type="number"
              min={0}
              value={data.targetMaxAge}
              onChange={(e) => onChange('targetMaxAge', e.target.value)}
              style={s.input}
              placeholder="לדוגמה 40"
            />
          </div>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>מותגים (מזהים מופרדים בפסיק, למשל 1,3)</label>
          <input
            type="text"
            value={data.targetBrandIds}
            onChange={(e) => onChange('targetBrandIds', e.target.value)}
            style={s.input}
            placeholder="ריק = כל המותגים"
          />
        </div>
      </div>
    </>
  );
};

const StoriesTab = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newStory, setNewStory] = useState(defaultNewStory());

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  // ── תצוגת לוח שנה (ברירת מחדל — נוח יותר לראות מה מוצג מתי) ──
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'table'
  const [monthAnchor, setMonthAnchor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dayDetailDate, setDayDetailDate] = useState(null); // יום שנלחץ בלוח — מציג את כל הסטוריז של אותו יום

  useEffect(() => { loadStories(); }, []);

  const loadStories = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await BOAPI.fetchStories();
      if (Array.isArray(result)) {
        const [statusObj, data] = result;
        if (statusObj[0]?.code === 0 && Array.isArray(data)) {
          setStories(data);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בטעינת הנתונים');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  // ── מיפוי "YYYY-MM-DD" → כל הסטוריז המוצגים באותו יום (לפי טווח startDate-endDate) —
  // מחושב פעם אחת לכל שינוי ברשימה, לא בכל רינדור של כל תא בלוח. ──
  const storiesByDate = useMemo(() => {
    const map = new Map();
    for (const item of stories) {
      const start = new Date(item.startDate);
      const end = new Date(item.endDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;
      let d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      let guard = 0;
      while (d <= endDay && guard < 400) { // guard — הגנה מטווח תאריכים שגוי/ענק
        const key = toDateInput(d);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
        d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
        guard++;
      }
    }
    return map;
  }, [stories]);

  const monthGrid = useMemo(() => buildMonthGrid(monthAnchor), [monthAnchor]);
  const today = new Date();

  const goPrevMonth = () => setMonthAnchor(m => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const goNextMonth = () => setMonthAnchor(m => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  const goToday = () => setMonthAnchor(new Date(today.getFullYear(), today.getMonth(), 1));

  // "+ סטורי חדש ליום זה" — נפתח מתוך פרטי היום בלוח, עם תאריכים ממולאים מראש
  const openAddForDate = (date) => {
    const key = toDateInput(date);
    setDayDetailDate(null);
    setNewStory({ ...defaultNewStory(), startDate: key, endDate: key });
    setShowAddForm(true);
  };

  // "ערוך" מתוך פרטי היום — סוגר את החלון של היום ופותח את עריכת הסטורי
  const editFromDayDetail = (item) => {
    setDayDetailDate(null);
    handleEdit(item);
  };

  const handleAddSubmit = async () => {
    const err = validateStory(newStory);
    if (err) { setError(err); return; }

    try {
      const kind = resolveKind(newStory.ext);
      const params = {
        name: newStory.name.trim(),
        ext: newStory.ext,
        imageUrl: newStory.imageUrl.trim(),
        videoUrl: kind === 'video' ? newStory.videoUrl.trim() : '',
        pdfUrl: kind === 'pdf' ? newStory.pdfUrl.trim() : '',
        extraMedia: cleanExtraMedia(newStory, kind),
        imageFit: kind === 'image' || kind === 'video' ? newStory.imageFit : '',
        description: newStory.description.trim(),
        startDate: toSqlStart(newStory.startDate),
        endDate: toSqlEnd(newStory.endDate),
        targetGender: newStory.targetGender,
        targetMinAge: newStory.targetMinAge,
        targetMaxAge: newStory.targetMaxAge,
        targetBrandIds: newStory.targetBrandIds.trim(),
        isCountable: kind === 'decision' && newStory.isCountable ? '1' : '0',
        countLabel: kind === 'decision' && newStory.isCountable ? newStory.countLabel.trim() : '',
      };
      const resp = await BOAPI.addStory(params);
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadStories();
          setShowAddForm(false);
          setNewStory(defaultNewStory());
          setError(null);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בהוספת הסטורי');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בהוספת הסטורי');
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewStory(defaultNewStory());
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      name: item.name || '',
      ext: item.ext || 'jpg',
      imageUrl: item.imageUrl || '',
      videoUrl: item.videoUrl || '',
      pdfUrl: item.pdfUrl || '',
      description: item.description || '',
      startDate: toDateInput(item.startDate),
      endDate: toDateInput(item.endDate),
      isActive: !!item.isActive,
      targetGender: item.targetGender || '',
      targetMinAge: item.targetMinAge ?? '',
      targetMaxAge: item.targetMaxAge ?? '',
      targetBrandIds: item.targetBrandIds || '',
      isCountable: !!item.isCountable,
      countLabel: item.countLabel || '',
      extraMedia: parseExtraMedia(item.mediaJson),
      imageFit: item.imageFit || '',
    });
  };

  const handleSaveEdit = async (id) => {
    const err = validateStory(editData);
    if (err) { setError(err); return; }

    try {
      const kind = resolveKind(editData.ext);
      const params = {
        id,
        name: editData.name.trim(),
        ext: editData.ext,
        imageUrl: editData.imageUrl.trim(),
        videoUrl: kind === 'video' ? editData.videoUrl.trim() : '',
        pdfUrl: kind === 'pdf' ? editData.pdfUrl.trim() : '',
        extraMedia: cleanExtraMedia(editData, kind),
        imageFit: kind === 'image' || kind === 'video' ? editData.imageFit : '',
        description: editData.description.trim(),
        startDate: toSqlStart(editData.startDate),
        endDate: toSqlEnd(editData.endDate),
        isActive: editData.isActive,
        targetGender: editData.targetGender,
        targetMinAge: editData.targetMinAge,
        targetMaxAge: editData.targetMaxAge,
        targetBrandIds: editData.targetBrandIds.trim(),
        isCountable: kind === 'decision' && editData.isCountable ? '1' : '0',
        countLabel: kind === 'decision' && editData.isCountable ? editData.countLabel.trim() : '',
      };
      const resp = await BOAPI.updateStory(params);
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadStories();
          setEditingId(null);
          setEditData(null);
          setError(null);
        } else {
          setError(statusObj[0]?.msg || 'שגיאה בעדכון הסטורי');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה בעדכון הסטורי');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`למחוק את הסטורי "${item.name}"?`)) return;
    try {
      const resp = await BOAPI.deleteStory(item.id);
      if (Array.isArray(resp)) {
        const [statusObj] = resp;
        if (statusObj[0]?.code === 0) {
          await loadStories();
        } else {
          setError(statusObj[0]?.msg || 'שגיאה במחיקת הסטורי');
        }
      }
    } catch (err) {
      setError(err.message || 'שגיאה במחיקת הסטורי');
    }
  };

  const formatDateRange = (start, end) => {
    const opts = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const s1 = new Date(start).toLocaleString('he-IL', opts);
    const s2 = new Date(end).toLocaleString('he-IL', opts);
    return `${s1} — ${s2}`;
  };

  if (loading) return <div style={s.center}>טוען סטוריז...</div>;

  const monthLabel = `${MONTH_NAMES[monthAnchor.getMonth()]} ${monthAnchor.getFullYear()}`;
  const dayDetailItems = dayDetailDate ? (storiesByDate.get(toDateInput(dayDetailDate)) || []) : [];

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <div style={s.titleRow}>
          <h2 style={s.title}>סטוריז</h2>
          {stories.length > 0 && <span style={s.countBadge}>{stories.length} סטוריז</span>}
        </div>
        <div style={s.headerActions}>
          <div style={s.viewToggle}>
            <button
              onClick={() => setViewMode('table')}
              style={{ ...s.viewToggleBtn, ...(viewMode === 'table' ? s.viewToggleBtnActive : {}) }}
            >
              📋 טבלה
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              style={{ ...s.viewToggleBtn, ...(viewMode === 'calendar' ? s.viewToggleBtnActive : {}) }}
            >
              📅 לוח שנה
            </button>
          </div>
          <button onClick={() => { setNewStory(defaultNewStory()); setShowAddForm(true); }} style={s.addButton}>➕ סטורי חדש</button>
        </div>
      </div>

      {error && (
        <div style={s.error}>
          ❌ {error}
          <button onClick={() => setError(null)} style={s.closeErrorButton}>✕</button>
        </div>
      )}

      {viewMode === 'table' ? (
        stories.length === 0 ? (
          <div style={s.noData}>אין סטוריז להצגה</div>
        ) : (
          <div style={s.tableWrapper}>
            <table style={s.table}>
              <thead>
                <tr style={s.headerRow}>
                  <th style={s.th}>תצוגה</th>
                  <th style={s.th}>שם</th>
                  <th style={s.th}>סוג</th>
                  <th style={s.th}>תאריכים</th>
                  <th style={s.th}>קהל יעד</th>
                  <th style={s.th}>באוויר</th>
                  <th style={s.th}>צפיות</th>
                  <th style={s.th}>ספירה</th>
                  <th style={s.th}>סטטוס</th>
                  <th style={s.th}>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {stories.map((item) => (
                  <tr key={item.id} style={s.row}>
                    <td style={s.td}>
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} style={s.thumb} />
                      ) : (
                        <div style={s.thumbEmpty}>—</div>
                      )}
                    </td>
                    <td style={s.td}>{item.name}</td>
                    <td style={s.td}>{EXT_LABEL[item.ext] || item.ext}</td>
                    <td style={s.td}>{formatDateRange(item.startDate, item.endDate)}</td>
                    <td style={s.td}>{describeTargeting(item)}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, backgroundColor: isLive(item) ? '#e8f5e9' : '#f0f0f0', color: isLive(item) ? '#2e7d32' : '#888' }}>
                        {isLive(item) ? '🟢 באוויר' : '⚪ לא באוויר'}
                      </span>
                    </td>
                    <td style={s.td}>{item.views || 0}</td>
                    <td style={s.td}>
                      {item.isCountable
                        ? `${item.countTotal || 0}${item.countLabel ? ` ${item.countLabel}` : ''}`
                        : item.tehillimRoundId
                        ? `${item.tehillimTakenCount || 0}/150 (${item.tehillimParticipantsCount || 0} משתתפים, סבב ${item.tehillimRoundNumber || 1})`
                        : '—'}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, backgroundColor: item.isActive ? '#e8f5e9' : '#ffebee', color: item.isActive ? '#2e7d32' : '#c62828' }}>
                        {item.isActive ? '✓ פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={s.actionButtons}>
                        <button onClick={() => handleEdit(item)} style={s.editButton}>✏️ ערוך</button>
                        <button onClick={() => handleDelete(item)} style={s.cancelButton}>🗑️ מחק</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <>
          <div style={s.monthNav}>
            <button onClick={goPrevMonth} style={s.navBtn}>חודש קודם</button>
            <span style={s.monthLabel}>{monthLabel}</span>
            <button onClick={goNextMonth} style={s.navBtn}>חודש הבא</button>
            <button onClick={goToday} style={s.todayBtn}>היום</button>
          </div>
          <div style={s.calendarWrap}>
            <div style={s.weekdayRow}>
              {WEEKDAY_LETTERS.map((w) => (
                <div key={w} style={s.weekdayCell}>{w}</div>
              ))}
            </div>
            <div style={s.grid}>
              {monthGrid.map((date) => {
                const key = toDateInput(date);
                const inMonth = date.getMonth() === monthAnchor.getMonth();
                const dayItems = storiesByDate.get(key) || [];
                const dToday = isSameDay(date, today);
                const visiblePills = dayItems.slice(0, 2);
                const extraCount = dayItems.length - visiblePills.length;
                return (
                  <div
                    key={key}
                    onClick={() => (dayItems.length > 0 ? setDayDetailDate(date) : openAddForDate(date))}
                    style={{
                      ...s.dayCell,
                      ...(inMonth ? {} : s.dayCellOutside),
                      ...(dayItems.length > 0 ? s.dayCellHasItems : {}),
                      ...(dToday ? s.dayCellToday : {}),
                    }}
                  >
                    <div style={s.dayCellTop}>
                      <span style={s.dayNumber}>{date.getDate()}</span>
                      {dayItems.length > 0 && <span style={s.itemsDot} />}
                    </div>
                    <div style={s.hebDateSmall}>{getHebrewDate(date)}</div>
                    <div style={s.pillsWrap}>
                      {visiblePills.map((item) => (
                        <div
                          key={item.id}
                          onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                          style={{ ...s.pill, ...(isLive(item) ? s.pillLive : s.pillInactive) }}
                          title={item.name}
                        >
                          {item.name}
                        </div>
                      ))}
                      {extraCount > 0 && (
                        <div
                          onClick={(e) => { e.stopPropagation(); setDayDetailDate(date); }}
                          style={s.morePill}
                        >
                          +{extraCount} נוספים
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* חלון "סטורי חדש" */}
      {showAddForm && (
        <div style={s.modalOverlay} onClick={handleCancelAdd}>
          <div style={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h3 style={s.formTitle}>סטורי חדש</h3>
              <button onClick={handleCancelAdd} style={s.closeErrorButton}>✕</button>
            </div>
            <StoryFields data={newStory} onChange={(field, value) => setNewStory((prev) => ({ ...prev, [field]: value }))} isNew />
            <div style={s.formButtons}>
              <button onClick={handleAddSubmit} style={s.saveButton}>➕ הוסף</button>
              <button onClick={handleCancelAdd} style={s.cancelButton}>✕ בטל</button>
            </div>
          </div>
        </div>
      )}

      {/* חלון עריכת סטורי */}
      {editingId != null && editData && (
        <div style={s.modalOverlay} onClick={handleCancelEdit}>
          <div style={s.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h3 style={s.formTitle}>עריכת סטורי</h3>
              <button onClick={handleCancelEdit} style={s.closeErrorButton}>✕</button>
            </div>
            <StoryFields data={editData} onChange={(field, value) => setEditData((prev) => ({ ...prev, [field]: value }))} />
            <div style={s.formGroup}>
              <label style={s.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editData.isActive}
                  onChange={(e) => setEditData((prev) => ({ ...prev, isActive: e.target.checked }))}
                  style={s.checkbox}
                />
                פעיל
              </label>
            </div>
            <div style={s.formButtons}>
              <button onClick={() => handleSaveEdit(editingId)} style={s.saveButton}>💾 שמור</button>
              <button onClick={handleCancelEdit} style={s.cancelButton}>✕ בטל</button>
            </div>
          </div>
        </div>
      )}

      {/* פרטי יום — כל הסטוריז של היום שנלחץ בלוח */}
      {dayDetailDate && (
        <div style={s.modalOverlay} onClick={() => setDayDetailDate(null)}>
          <div style={s.dayDetailPanel} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h3 style={s.formTitle}>{getHebrewDate(dayDetailDate)} · {dayDetailDate.toLocaleDateString('he-IL')}</h3>
              <button onClick={() => setDayDetailDate(null)} style={s.closeErrorButton}>✕</button>
            </div>
            {dayDetailItems.length === 0 ? (
              <div style={s.noData}>אין סטוריז ביום הזה</div>
            ) : (
              dayDetailItems.map((item) => (
                <div key={item.id} style={s.dayDetailRow}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.name} style={s.thumb} />
                  ) : (
                    <div style={s.thumbEmpty}>—</div>
                  )}
                  <div style={s.dayDetailInfo}>
                    <div style={s.dayDetailName}>{item.name}</div>
                    <div style={s.dayDetailMeta}>
                      {EXT_LABEL[item.ext] || item.ext} ·{' '}
                      <span style={{ color: isLive(item) ? '#2e7d32' : '#888' }}>{isLive(item) ? '🟢 באוויר' : '⚪ לא באוויר'}</span>
                      {!item.isActive && <span style={{ color: '#c62828' }}> · לא פעיל</span>}
                    </div>
                  </div>
                  <div style={s.actionButtons}>
                    <button onClick={() => editFromDayDetail(item)} style={s.editButton}>✏️ ערוך</button>
                    <button onClick={() => handleDelete(item)} style={s.cancelButton}>🗑️ מחק</button>
                  </div>
                </div>
              ))
            )}
            <button onClick={() => openAddForDate(dayDetailDate)} style={s.addDayButton}>➕ סטורי חדש ליום זה</button>
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
  titleRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },
  countBadge: { backgroundColor: '#eef1f6', color: '#555', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  headerActions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  addButton: { padding: '10px 20px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },

  // מתג תצוגה (טבלה / לוח שנה)
  viewToggle: { display: 'flex', backgroundColor: '#eef1f6', borderRadius: '8px', padding: '3px', gap: '2px' },
  viewToggleBtn: { padding: '7px 14px', border: 'none', background: 'transparent', color: '#555', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  viewToggleBtnActive: { backgroundColor: '#fff', color: '#1f6feb', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' },

  // לוח שנה — אותו דפוס כמו HakdashotTab.jsx
  monthNav: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  navBtn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 'bold', color: '#334155' },
  monthLabel: { fontSize: 16, fontWeight: 'bold', color: '#1a1f36', minWidth: 140, textAlign: 'center' },
  todayBtn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #C9A84C', background: 'rgba(201,168,76,0.1)', color: '#8a6d1f', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' },

  calendarWrap: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '12px', overflow: 'hidden' },
  weekdayRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', direction: 'rtl' },
  weekdayCell: { textAlign: 'center', padding: '8px 0', fontSize: 12, fontWeight: 'bold', color: '#94A3B8' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '118px', gap: '4px', direction: 'rtl' },

  dayCell: {
    border: '1px solid #eee', borderRadius: '8px', padding: '6px', cursor: 'pointer',
    overflow: 'hidden', backgroundColor: '#fff', transition: 'background-color 0.15s', display: 'flex', flexDirection: 'column',
  },
  dayCellOutside: { backgroundColor: '#fafafa', opacity: 0.6 },
  dayCellHasItems: { backgroundColor: 'rgba(31,111,235,0.05)', borderColor: 'rgba(31,111,235,0.3)' },
  dayCellToday: { borderColor: '#1f6feb', borderWidth: '2px' },

  dayCellTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  dayNumber: { fontSize: 13, fontWeight: 'bold', color: '#333' },
  itemsDot: { width: 7, height: 7, borderRadius: '50%', backgroundColor: '#1f6feb' },
  hebDateSmall: { fontSize: 10, color: '#94A3B8', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },

  pillsWrap: { display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '4px', overflow: 'hidden' },
  pill: {
    fontSize: 10, padding: '2px 6px', borderRadius: '6px', whiteSpace: 'nowrap', overflow: 'hidden',
    textOverflow: 'ellipsis', cursor: 'pointer', fontWeight: 'bold',
  },
  pillLive: { backgroundColor: 'rgba(46,125,50,0.12)', color: '#2e7d32' },
  pillInactive: { backgroundColor: '#f0f0f0', color: '#888' },
  morePill: { fontSize: 10, padding: '2px 6px', color: '#1f6feb', cursor: 'pointer', fontWeight: 'bold' },

  // חלונות מודאליים (הוספה/עריכה/פרטי יום)
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15,23,42,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
  },
  modalPanel: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  dayDetailPanel: {
    backgroundColor: '#fff', borderRadius: '12px', padding: '24px',
    width: '100%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  },
  panelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' },

  dayDetailRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f0f0f0' },
  dayDetailInfo: { flex: 1, minWidth: 0 },
  dayDetailName: { fontSize: '14px', fontWeight: 'bold', color: '#1a1f36' },
  dayDetailMeta: { fontSize: '12px', color: '#888', marginTop: '2px' },
  addDayButton: { marginTop: '16px', width: '100%', padding: '10px', backgroundColor: '#eef1f6', color: '#1f6feb', border: '1px dashed #1f6feb', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },

  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' },
  formTitle: { marginTop: 0, marginBottom: '20px', color: '#333', fontSize: '18px' },
  formGroup: { marginBottom: '15px' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'ltr', textAlign: 'right', boxSizing: 'border-box' },
  extraMediaRow: { display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px' },
  addExtraButton: { padding: '8px 14px', backgroundColor: '#eef1f6', color: '#333', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' },
  uploadRow: { display: 'flex', gap: '8px', alignItems: 'stretch' },
  uploadButton: { flex: '0 0 auto', padding: '10px 14px', backgroundColor: '#eef1f6', color: '#333', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  hiddenFileInput: { display: 'none' },
  textarea: { width: '100%', padding: '10px', fontFamily: 'Arial, sans-serif', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', boxSizing: 'border-box', resize: 'vertical' },
  checkbox: { marginLeft: '5px', cursor: 'pointer' },
  checkboxLabel: { display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' },

  targetSection: { marginTop: '10px', paddingTop: '15px', borderTop: '1px dashed #ddd' },
  warningNote: { fontSize: '13px', color: '#8a6d1d', backgroundColor: '#fff8e1', border: '1px solid #f0dca0', borderRadius: '4px', padding: '10px 12px', marginBottom: '12px' },
  targetTitle: { fontSize: '13px', color: '#697386', fontWeight: 'bold', marginBottom: '12px' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' },

  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '15px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'middle' },
  thumb: { width: 48, height: 48, borderRadius: 8, objectFit: 'cover' },
  thumbEmpty: { width: 48, height: 48, borderRadius: 8, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' },

  statusBadge: { display: 'inline-block', padding: '6px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' },
  actionButtons: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  editButton: { padding: '8px 16px', backgroundColor: '#1f6feb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  saveButton: { padding: '8px 14px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },
  cancelButton: { padding: '8px 14px', backgroundColor: '#c62828', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default StoriesTab;
